import clsx from "clsx";
import { CheckCircle2, Info, TriangleAlert, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

type ToastTone = "info" | "ok" | "danger";

interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  detail?: string | undefined;
}

type Notify = (toast: Omit<Toast, "id">) => void;

const Context = createContext<Notify>(() => {});

/** Shows a short notice; errors stay until dismissed, the rest fade after a few seconds. */
export const useToast = () => useContext(Context);

const AUTO_CLOSE_MS = 5000;

const ICON = {
  info: <Info className="size-5 text-info" aria-hidden />,
  ok: <CheckCircle2 className="size-5 text-ok" aria-hidden />,
  danger: <TriangleAlert className="size-5 text-danger" aria-hidden />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const dismiss = useCallback(
    (id: number) => setToasts((all) => all.filter((x) => x.id !== id)),
    [],
  );
  const notify = useCallback<Notify>(
    (toast) => {
      const id = nextId.current++;
      setToasts((all) => [...all.slice(-3), { ...toast, id }]);
      if (toast.tone !== "danger") setTimeout(() => dismiss(id), AUTO_CLOSE_MS);
    },
    [dismiss],
  );
  const value = useMemo(() => notify, [notify]);
  return (
    <Context.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 md:bottom-6 md:right-6 md:left-auto md:items-end"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "danger" ? "alert" : "status"}
            className={clsx(
              "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-surface px-4 py-3 text-sm shadow-lg",
              toast.tone === "danger" ? "border-danger" : "border-line",
            )}
          >
            {ICON[toast.tone]}
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-medium">{toast.title}</span>
              {toast.detail && <span className="text-xs text-muted">{toast.detail}</span>}
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="rounded p-0.5 text-muted hover:text-text"
              aria-label={t("common.close")}
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </Context.Provider>
  );
}
